import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import App from './App';
import { AuthProvider } from '@/contexts/AuthContext';
import { SidebarProvider } from '@/contexts/SidebarContext';
import { GoogleLoginProvider } from '@/contexts/GoogleLoginProvider';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { criarQueryCacheComAviso } from '@/lib/aviso-carga';
import { initSentry } from '@/lib/sentry';
import {
  instalarRegistroDeErros,
  conectarTokenParaRegistroDeErros,
  reportarErroDeTela,
} from '@/lib/registro-erros';
import { getAuthToken } from '@/lib/api';
import './styles/index.css';

// ============================================================
// Inicialização
// ============================================================

// Sentry primeiro (precisa estar pronto antes de qualquer erro)
initSentry();

// FUNDACOES A1 (17/08): registro de erros de tela NO SERVIDOR — o Sentry do
// beta está OFF (env), e mesmo ligado o admin não o abre. Erro de JS,
// promessa rejeitada e falha de render viram linha em Configurações ›
// Sistema › Erros de tela. O registro nunca derruba a tela (catch em tudo).
instalarRegistroDeErros();
conectarTokenParaRegistroDeErros(getAuthToken);

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dados ficam frescos por 30s antes de refetch automático
      staleTime: 30_000,
      // Retentativa em caso de falha (Sentry já captura erros)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
  // D-3 (AUDITORIA-COMERCIAL): carga que falha AVISA — sem isto, 500/queda de
  // rede deixava o kanban "vazio legítimo" e o vendedor achava que não havia
  // trabalho. Toast único dedupado (ver src/lib/aviso-carga.ts).
  queryCache: criarQueryCacheComAviso(),
});

// ErrorBoundary global pra capturar erros React + reportar pro Sentry
// eslint-disable-next-line react-refresh/only-export-components
const SentryFallback = () => (
  <div className="min-h-screen flex items-center justify-center p-6 bg-paper dark:bg-paper-dark">
    <div className="max-w-md text-center">
      <h1 className="font-display text-4xl text-red mb-4">Algo deu errado</h1>
      <p className="text-ink-2 dark:text-ink-dark-2 mb-6">
        Um erro inesperado ocorreu. Já fomos notificados e estamos investigando.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="bg-red text-white px-6 py-2 rounded-pill font-medium hover:bg-red-deep transition-colors"
      >
        Recarregar
      </button>
    </div>
  </div>
);

// ============================================================
// Render
// ============================================================

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={<SentryFallback />}
      beforeCapture={(_scope, error) => {
        // A1: falha de RENDER (boundary) também vira linha no registro.
        reportarErroDeTela({
          tipo: 'render',
          mensagem: (error as Error | null)?.message ?? 'falha ao renderizar',
          stack: (error as Error | null)?.stack ?? null,
        });
      }}
    >
      <QueryClientProvider client={queryClient}>
        {/* A-1 (auditoria 13/08): opt-in antecipado nos dois comportamentos do
            v7 — mata os 2 warnings de future-flag em TODA carga de página.
            v7_relativeSplatPath é no-op aqui: a única rota splat (path="*",
            NotFoundPage) só usa link absoluto. */}
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <GoogleLoginProvider>
            <AuthProvider>
              <ThemeProvider>
                <SidebarProvider>
                  <App />
                </SidebarProvider>
              </ThemeProvider>
            </AuthProvider>
          </GoogleLoginProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
